import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Mail, Lock, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { signInStartAsync } from "../../redux/user/userActions";
import { useDispatch } from "react-redux";

const formSchema = (t) => z.object({
  email: z.string()
    .min(1, { message: t('loginpage.errors.emailRequired', "Email is required.") })
    .email({ message: t('loginpage.errors.emailInvalid', "Invalid email address.") }),
  password: z.string()
    .min(1, { message: t('loginpage.errors.passwordRequired', "Password is required.") })
    // .min(8, { message: t('loginpage.errors.passwordMin', "Password must be at least 8 characters.") })
});

export default function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (values) => {
    setIsSubmitting(true);
    console.log("Form submitted with:", values);
    dispatch(signInStartAsync(values, navigate, () => setIsSubmitting(false)));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center text-muted-foreground">
                <Mail size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                {t("loginpage.fields.email", "Email Address")}
              </FormLabel>
              <FormControl>
                <Input 
                  type="email" 
                  placeholder={t("loginpage.placeholders.email", "you@example.com")}
                  {...field} 
                  className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel className="flex items-center text-muted-foreground">
                  <Lock size={14} className={cn("text-muted-foreground/80", isRTL ? "ml-1.5" : "mr-1.5")} />
                  {t("loginpage.fields.password", "Password")}
                </FormLabel>
                <Button 
                  type="button" 
                  variant="link"
                  size="sm"
                  onClick={() => console.log('Forgot password clicked')} // Placeholder
                  className="p-0 h-auto text-xs text-primary hover:underline"
                >
                  {t('loginpage.forgotPassword', 'Forgot password?')}
                </Button>
              </div>
              <FormControl>
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder={t("loginpage.placeholders.password", "••••••••")}
                    {...field} 
                    className="bg-background/80 focus:bg-background placeholder:text-muted-foreground/50"
                  />
                  <Button 
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn("absolute h-7 w-7 text-muted-foreground hover:text-foreground", isRTL ? "left-1.5 top-1/2 -translate-y-1/2" : "right-1.5 top-1/2 -translate-y-1/2")}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('common.hidePassword', 'Hide password') : t('common.showPassword', 'Show password')}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full group flex items-center justify-center gap-2" 
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <motion.div
              animate={{ rotate: 360 }} 
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full"
            />
          ) : (
            <LogIn size={18} className={cn("transition-transform", isRTL ? "group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5")} />
          )}
          {isSubmitting 
            ? t('loginpage.submitting', 'Logging in...') 
            : t("loginpage.button", "Login")}
        </Button>

        {/* Removed the Sign Up button from here, will be in CardFooter of login.page.jsx */}
      </form>
    </Form>
  );
}
